import { type MutableRefObject, useCallback, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { clampSplitPx, saveSplitPx } from '../sessions-panel-split-storage';

const SPLIT_SAVE_DEBOUNCE_MS = 200;

type UseSessionsSplitCloseParams = {
  storageKey: string;
  /**
   * The list+detail (workspace) width, not the outer wrapper — Evidence's share
   * shrinks the workspace below the wrapper, and `pct` here is the list's share of
   * that inner group. Persisting against the wrapper would store an inflated width
   * that restores wrong with Evidence open; this must match the drag ceiling.
   */
  workspaceWidthRef: MutableRefObject<number>;
  /** Shared with the controls hook; true while an open/close/restore settles. */
  isProgrammaticResizeRef: MutableRefObject<boolean>;
  /** False until startup has applied its final geometry. */
  isPersistenceReadyRef: MutableRefObject<boolean>;
};

/**
 * Persists the user's list↔detail split width across pointer drags and keyboard
 * resizes. There is no drag-to-close-detail path: the width ceiling
 * (`computeMaxSplitPx`, fed to the list's `maxSize`) always reserves
 * `MIN_DETAIL_PX` for the detail, so the divider can't be dragged far enough to
 * collapse it — returning to the list is a header button (`panelActions.toList`).
 * Rationale in ../sessions-panel-split-storage.docs.md.
 *
 * Kept named `...Close` (it now only persists — the drag-close it was named for
 * was retired) so the file stays on its grandfathered carved-domain path: the
 * migration UI-intake ratchet reads a rename as a new `@heidi/session` module and
 * fails the gate.
 */
export const useSessionsSplitClose = ({
  storageKey,
  workspaceWidthRef,
  isProgrammaticResizeRef,
  isPersistenceReadyRef,
}: UseSessionsSplitCloseParams) => {
  // Debounced so a drag's/keyboard's intermediate frames coalesce to the final
  // width. The pixel value (not %) is stored so the preference survives screen
  // size changes.
  const debouncedSaveSplitPx = useDebouncedCallback(
    (key: string, px: number) => saveSplitPx(key, px),
    SPLIT_SAVE_DEBOUNCE_MS
  );

  // Persist on any user-driven resize — pointer drag OR keyboard (both reach
  // here via `onResize`). Programmatic resizes are excluded by the settle
  // window, and `pct <= 0` skips a list collapse.
  const onLeftPanelResize = useCallback(
    (pct: number) => {
      if (!isPersistenceReadyRef.current || isProgrammaticResizeRef.current || pct <= 0) {
        return;
      }
      const width = workspaceWidthRef.current;
      if (!width) return;
      // Clamp before persisting (defensive — the live `maxSize` already caps the
      // drag) so a stored width always keeps the detail above its minimum.
      debouncedSaveSplitPx(storageKey, clampSplitPx(width, (pct / 100) * width));
    },
    [
      debouncedSaveSplitPx,
      storageKey,
      workspaceWidthRef,
      isProgrammaticResizeRef,
      isPersistenceReadyRef,
    ]
  );

  // Flush the pending width on drag end so a quick navigate within the sessions
  // route — which keeps this provider mounted, so the unmount flush won't fire —
  // still persists the released width.
  const onRightHandleDragging = useCallback(
    (isDragging: boolean) => {
      if (!isDragging) debouncedSaveSplitPx.flush();
    },
    [debouncedSaveSplitPx]
  );

  // Flush any pending width save on unmount (leaving the sessions route).
  useEffect(
    () => () => {
      debouncedSaveSplitPx.flush();
    },
    [debouncedSaveSplitPx]
  );

  return { onLeftPanelResize, onRightHandleDragging };
};
